import { createElement } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

const registry = {};
const renderObject = (props) => createElement(registry.object, props);
const renderField = (props) => createElement(registry.field, props);

const LABELS = {
  eyebrow: "Etiqueta superior", title: "Título", title_lines: "Título (una línea por renglón)", text: "Texto", text_2: "Texto 2",
  lines: "Frases (una por línea)", items: "Elementos (uno por línea)", paragraphs: "Párrafos (uno por línea)", intro: "Introducción (un párrafo por línea)",
  points: "Puntos (uno por línea)", steps: "Pasos", cta_primary: "Botón principal", cta_secondary: "Botón secundario", primary: "Botón principal",
  secondary: "Botón secundario", closing: "Cierre", closing_title: "Título de cierre", closing_text: "Texto de cierre", flow: "Flujo (uno por línea)",
  flow_notes: "Notas del flujo (una por línea)", flow_lines: "Frases del flujo (una por línea)", chain: "Cadena (uno por línea)", chain_notes: "Notas de la cadena",
  list: "Países disponibles (uno por línea)", labels: "Etiquetas del formulario", disclaimer: "Texto obligatorio del checkbox", submit: "Botón de envío",
  show_on_site: "Mostrar en la página", direct_pct: "Comisión directa (%)", team_pct: "Comisión de equipo (%)", recurring_pct: "Comisión recurrente (%)",
  plan_name: "Nombre del plan", notes: "Notas", terminos: "Términos y condiciones", privacidad: "Aviso de privacidad", aviso_legal: "Quiénes somos",
  company_name: "Razón social", tax_id: "RFC", domicilio: "Domicilio", representante: "Representante legal / responsable", video_url: "URL de video (YouTube, Vimeo o .mp4)", video_title: "Título del video",
  soluciones_images: "Imágenes de tarjetas anchas (una URL por línea)", manifiesto_images: "Imágenes del manifiesto, una por punto (una URL por línea)", values: "Valores", slogan: "Eslogan (una línea por renglón)",
};
const LONG = ["terminos", "privacidad", "aviso_legal"];

export const labelFor = (key) => LABELS[key] || key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const isImageKey = (k) => /image/.test(k);

export const FieldEditor = ({ name, value, onChange, testId }) => {
  const id = testId || `field-${name}`;
  if (typeof value === "string") {
    if (name.startsWith("show_")) {
      return (
        <div className="flex items-center justify-between rounded-xl border border-ink/10 bg-white p-4">
          <span className="text-sm font-semibold">{labelFor(name)}</span>
          <Switch checked={value === "si"} onCheckedChange={(v) => onChange(v ? "si" : "no")} data-testid={id} />
        </div>
      );
    }
    const long = LONG.includes(name) || value.length > 80;
    return (
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-ink/70">{labelFor(name)}</label>
        {long ? (
          <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={LONG.includes(name) ? 14 : 3} className="bg-white" data-testid={id} />
        ) : (
          <Input value={value} onChange={(e) => onChange(e.target.value)} className="bg-white" data-testid={id} />
        )}
        {isImageKey(name) && value && <img src={value} alt="" className="mt-2 h-24 w-40 rounded-lg object-cover" />}
      </div>
    );
  }
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return (
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-ink/70">{labelFor(name)}</label>
        <Textarea value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} rows={Math.min(Math.max(value.length + 1, 3), 14)} className="bg-white" data-testid={id} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink/70">{labelFor(name)}</p>
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="space-y-3 rounded-xl border border-ink/10 bg-paper/60 p-4">
              <p className="font-mono text-xs text-ink/60">#{i + 1}</p>
              {renderObject({ value: item, onChange: (v) => onChange(value.map((x, j) => (j === i ? v : x))), prefix: `${id}-${i}` })}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className="rounded-xl border border-ink/10 bg-paper/60 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.06em] text-ink/70">{labelFor(name)}</p>
        {renderObject({ value, onChange, prefix: id })}
      </div>
    );
  }
  return null;
};

export const ObjectEditor = ({ value, onChange, prefix }) => (
  <div className="space-y-4">
    {Object.keys(value).map((k) =>
      renderField({ key: k, name: k, value: value[k], onChange: (v) => onChange({ ...value, [k]: v }), testId: `${prefix}-${k}` }),
    )}
  </div>
);

registry.object = ObjectEditor;
registry.field = FieldEditor;

export function cleanContent(node) {
  if (Array.isArray(node)) {
    if (node.every((v) => typeof v === "string")) return node.map((s) => s.trim()).filter(Boolean);
    return node.map(cleanContent);
  }
  if (node && typeof node === "object") {
    return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, cleanContent(v)]));
  }
  return node;
}
